/**
 * PP Products Drawer — lazy-loaded vía Section Rendering API.
 *
 * El header solo monta el botón "Productos" + un contenedor vacío
 * (#PpProductsDrawerMount). El markup del drawer (3 niveles + ~448 thumbnails)
 * se trae bajo demanda con fetch a `?section_id=pp-products-drawer`, se inyecta
 * UNA vez y se cachea. Prefetch en hover/focus (desktop) e idle (mobile) para
 * que al abrir ya esté listo. Fallback: el botón es un <a href> real a
 * /collections, así que sin JS o si el fetch falla, "Productos" igual navega.
 */

class PpProductsDrawer {
  constructor() {
    this.mount = document.getElementById('PpProductsDrawerMount');
    this.triggers = Array.from(document.querySelectorAll('[data-pp-drawer-trigger]'));
    if (!this.mount || !this.triggers.length) return;

    this.drawer = null;
    this.loaded = false;
    this._loading = null;
    this._onDocClick = this._onDocClick.bind(this);
    this._onDocKey = this._onDocKey.bind(this);

    this.bindTriggers();
  }

  bindTriggers() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      });
      // Prefetch: al pasar el cursor o enfocar, ya vamos trayendo el drawer.
      trigger.addEventListener('mouseenter', () => this.load());
      trigger.addEventListener('focus', () => this.load());
    });

    // Mobile / sin hover: precarga en reposo para que el primer tap abra ya listo.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.load(), { timeout: 3000 });
    } else {
      window.setTimeout(() => this.load(), 2000);
    }
  }

  /** Trae e inyecta el drawer una sola vez. Idempotente; devuelve una promesa. */
  load() {
    if (this.loaded) return Promise.resolve();
    if (this._loading) return this._loading;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}?section_id=pp-products-drawer`;

    this._loading = fetch(url)
      .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
      .then((html) => {
        // <template> parsea el HTML de forma inerte: no carga imágenes ni
        // ejecuta nada hasta insertar. El contenido es de primera parte
        // (Section Rendering API del propio theme, ya escapado por Liquid).
        const tmp = document.createElement('template');
        tmp.innerHTML = html;
        const drawer = tmp.content.querySelector('#PpProductsDrawer');
        if (!drawer) return;

        this.mount.appendChild(drawer);
        this.drawer = drawer;
        this.panel = drawer.querySelector('[data-pp-drawer-panel]');
        this.slot2 = drawer.querySelector('[data-pp-slot="2"]');
        this.slot3 = drawer.querySelector('[data-pp-slot="3"]');
        this.closers = Array.from(drawer.querySelectorAll('[data-pp-drawer-close]'));
        this.bind();
        this.loaded = true;
      })
      .catch(() => {
        // Falla la red → el <a href> del botón hace su trabajo la próxima.
        this._loading = null;
      });

    return this._loading;
  }

  /** Engancha los eventos del contenido ya inyectado (antes vivía en el constructor). */
  bind() {
    this.closers.forEach((closer) => {
      closer.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    });

    this.drawer.querySelectorAll('[data-pp-level="1"]').forEach((item) => {
      const targetId = item.dataset.ppTarget;
      if (!targetId) return;
      const showPanel = () => this.showSlot(2, targetId, item);
      item.addEventListener('mouseenter', showPanel);
      item.addEventListener('focus', showPanel);
    });

    this.drawer.querySelectorAll('[data-pp-level="2"]').forEach((item) => {
      const targetId = item.dataset.ppTarget;
      if (!targetId) return;
      const showPanel = () => this.showSlot(3, targetId, item);
      item.addEventListener('mouseenter', showPanel);
      item.addEventListener('focus', showPanel);
    });
  }

  async toggle() {
    if (this.isOpen) {
      this.close();
      return;
    }
    await this.load();
    if (this.drawer) this.open();
  }

  get isOpen() {
    return !!this.drawer && this.drawer.classList.contains('is-open');
  }

  open() {
    // Se recuerda quién tenía el foco para devolvérselo al cerrar.
    this.lastFocus = document.activeElement;
    this.setTopOffset();
    this.drawer.hidden = false;
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('pp-products-drawer-open');
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));

    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
    });

    const firstFocusable = this.drawer.querySelector('.pp-products-drawer__item');
    firstFocusable?.focus({ preventScroll: true });

    window.setTimeout(() => {
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onDocKey);
    }, 0);
  }

  close() {
    if (!this.drawer) return;
    /*
     * DEVOLVER EL FOCO AL CERRAR.
     *
     * Este panel declara `aria-modal`, así que mientras está abierto la ayuda
     * técnica trata todo lo de atrás como oculto. Al cerrar sin devolver el
     * foco, éste cae a <body> y la tabulación reinicia desde el logo: quien
     * navega con teclado pierde su lugar a media navegación de catálogo.
     *
     * `pp-cart-drawer.js` ya lo hacía bien; esto lo empareja.
     * `preventScroll` para que devolver el foco no brinque la página.
     */
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      try { this.lastFocus.focus({ preventScroll: true }); } catch (e) { /* nodo ya no existe */ }
    }
    this.lastFocus = null;

    this.drawer.classList.remove('is-open');
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('pp-products-drawer-open');

    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onDocKey);

    window.setTimeout(() => {
      this.drawer.hidden = true;
      this.drawer.setAttribute('aria-hidden', 'true');
      this.hideSlot(2);
      this.hideSlot(3);
      this.drawer.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
    }, 320);
  }

  setTopOffset() {
    const wrap = document.querySelector('sticky-header, .header-wrapper');
    let offset = 100;
    if (wrap) {
      offset = Math.max(0, wrap.getBoundingClientRect().bottom);
    }
    this.drawer.style.setProperty('--pp-drawer-top', `${offset}px`);
  }

  showSlot(level, targetId, triggerItem) {
    const slot = level === 2 ? this.slot2 : this.slot3;
    if (!slot) return;

    slot.querySelectorAll('[data-pp-for]').forEach((panel) => {
      panel.hidden = true;
    });

    const match = slot.querySelector(`[data-pp-for="${targetId}"]`);
    if (match) {
      match.hidden = false;
      slot.hidden = false;
    } else {
      slot.hidden = true;
    }

    this.markActive(triggerItem, level - 1);

    if (level === 2) {
      this.hideSlot(3);
    }
  }

  hideSlot(level) {
    const slot = level === 2 ? this.slot2 : this.slot3;
    if (!slot) return;
    slot.hidden = true;
    slot.querySelectorAll('[data-pp-for]').forEach((panel) => {
      panel.hidden = true;
    });
  }

  markActive(item, siblingLevel) {
    const siblingSelector = `[data-pp-level="${siblingLevel}"].is-active`;
    this.drawer.querySelectorAll(siblingSelector).forEach((el) => el.classList.remove('is-active'));
    item.classList.add('is-active');
  }

  _onDocClick(e) {
    if (this.panel && this.panel.contains(e.target)) return;
    if (this.triggers.some((t) => t.contains(e.target))) return;
    this.close();
  }

  _onDocKey(e) {
    if (e.key === 'Escape') this.close();
  }
}

if (document.readyState !== 'loading') {
  new PpProductsDrawer();
} else {
  document.addEventListener('DOMContentLoaded', () => new PpProductsDrawer());
}
